import * as Api from '../lib/typescript-axios';
import { AssessmentClient } from '../lib/assessment-client';
import * as Errors from '../lib/errors';
import { AssessmentEngine } from './assessment-engine';
import { ReportEngine } from './report-engine';
import { getIdFromIri } from './utils';

type PageDisplayType = (typeof ChildAssessmentEngine.PAGE_DISPLAY_TYPES)[number];

export interface PageWithQuestions {
  id: string;
  instructions: Array<Api.InstructionFullRead>;
  questions?: Array<Api.QuestionFullRead>;
}

export class ChildAssessmentEngine extends AssessmentEngine {
  public static readonly STATE_RETURN_TO_PARENT_ASSESSMENT = 'ReturnToParentAssessment';
  public static readonly PAGE_DISPLAY_TYPE_MEMORY = 'memory';
  public static readonly PAGE_DISPLAY_TYPE_SINGLE = 'single';
  public static readonly PAGE_DISPLAY_TYPE_MULTIPLE = 'multiple';
  public static readonly PAGE_DISPLAY_TYPES = [
    ChildAssessmentEngine.PAGE_DISPLAY_TYPE_MEMORY,
    ChildAssessmentEngine.PAGE_DISPLAY_TYPE_SINGLE,
    ChildAssessmentEngine.PAGE_DISPLAY_TYPE_MULTIPLE,
  ];

  private questionMap: Map<string, Api.QuestionFullRead> = new Map();
  private pageDisplayType: PageDisplayType;
  private pagesWithQuestions: PageWithQuestions[];

  public constructor(
    userId: string,
    client: AssessmentClient,
    assessment: Api.AssessmentFullRead,
    hasCareers: boolean,
    hasBadges: boolean,
  ) {
    if (
      assessment.aptitudeSectionConfiguration === undefined ||
      assessment.aptitudeSectionConfiguration === null ||
      !ChildAssessmentEngine.PAGE_DISPLAY_TYPES.includes(assessment.aptitudeSectionConfiguration.pageDisplayType)
    ) {
      throw new Errors.AssessmentInvalidStateError('AptitudeSectionConfiguration is missing or invalid');
    }

    super(userId, client, assessment, hasCareers, hasBadges);

    this.questionMap = this.initializeQuestionMap();
    this.pagesWithQuestions = this.initializePagesWithQuestions();
    this.pageDisplayType = this.assessment.aptitudeSectionConfiguration.pageDisplayType;
  }

  protected initializeQuestionMap(): Map<string, Api.QuestionFullRead> {
    const questionMap = new Map();

    this.assessment.questions.forEach((question) => {
      questionMap.set(question.id, question);
    });

    return questionMap;
  }

  protected initializePagesWithQuestions(): PageWithQuestions[] {
    const pagesWithQuestions: PageWithQuestions[] = [];

    this.assessment.pages.forEach((page) => {
      const pageWithQuestions: PageWithQuestions = {
        id: page.id,
        instructions: page.instructions,
        questions: [],
      };

      if (page.questions) {
        page.questions.forEach((questionIri) => {
          const question = this.questionMap.get(getIdFromIri(questionIri));
          if (question) {
            pageWithQuestions.questions.push(question);
          } else {
            throw new Errors.AssessmentInvalidStateError(`Page Question not found: ${questionIri}`);
          }
        });
      }

      pagesWithQuestions.push(pageWithQuestions);
    });

    return pagesWithQuestions;
  }

  public getPageDisplayType(): PageDisplayType {
    return this.pageDisplayType;
  }

  public getPagesWithQuestions(): PageWithQuestions[] {
    return this.pagesWithQuestions;
  }

  public async startTimer(): Promise<Api.TimerFullRead> {
    return await this.assessmentClient.startTimer(this.assessment.id);
  }

  public async stopTimer(): Promise<Api.TimerFullRead> {
    return await this.assessmentClient.stopTimer(this.assessment.id);
  }

  public async getTimer(): Promise<Api.TimerFullRead> {
    return await this.assessmentClient.getTimer(this.assessment.id);
  }

  public async answerQuestion(questionId: string, answerId: string): Promise<string> {
    if (ChildAssessmentEngine.PAGE_DISPLAY_TYPE_MEMORY === this.pageDisplayType) {
      return super.answerQuestion(questionId, answerId);
    }

    return this.mutex.use(async () => {
      if (this.canAnswerQuestion()) {
        const question = this.questionMap.get(questionId);
        const questionAnswer = question.answers.find((answer) => answer.id === answerId);
        let userAnswer = this.currentUserAnswers.get(questionId);

        if (question && questionAnswer) {
          if (userAnswer) {
            this.currentUserAnswers.set(
              questionId,
              await this.assessmentClient.updateQuestionAnswer(this.assessment.id, answerId, userAnswer.id),
            );
          } else {
            userAnswer = await this.assessmentClient.answerQuestion(this.assessment.id, answerId);
            this.assessment.userAnswers.push(userAnswer);
            this.currentUserAnswers.set(questionId, userAnswer);
          }

          return this.assessment.status;
        }

        const errorMessage = `invalid answerId: ${answerId} for question ${question?.id} (questionId ${questionId} sent)`;

        throw new Errors.AssessmentInvalidStateError(errorMessage);
      }

      throw new Errors.AssessmentInvalidStateError(`invalid state: ${this.assessment.status}`);
    });
  }

  public async getReportEngine(enableReportBlocking = false): Promise<ReportEngine> {
    if (
      this.assessment.status === ChildAssessmentEngine.STATE_RETURN_TO_PARENT_ASSESSMENT &&
      this.assessment.parentAssessment !== undefined
    ) {
      const parentAssessmentMinimumRead = await this.assessmentClient.getMinimumAssessment(this.assessment.parentAssessment);
      const achieveWorksReportId = AssessmentClient.getRawId(parentAssessmentMinimumRead.achieveWorksReport);

      if (achieveWorksReportId !== undefined) {
        return ReportEngine.getReportEngine(
          this.assessmentClient.getBasePath(),
          this.userId,
          parentAssessmentMinimumRead.status,
          this.assessmentClient.getToken(),
          achieveWorksReportId,
          this.hasCareers,
          this.hasBadges,
          enableReportBlocking,
          parentAssessmentMinimumRead.isResultReviewed,
          this.assessmentClient.getLocale(),
        );
      }
    }

    throw new Errors.AssessmentInvalidStateError(`invalid state: ${this.assessment.status}`);
  }

  public setNextState(): string {
    switch (this.assessment.status) {
      case AssessmentEngine.STATE_ANSWER_QUESTIONS: {
        this.assessment.status = AssessmentEngine.STATE_CALCULATE_ASSESSMENT;
        break;
      }

      case AssessmentEngine.STATE_CALCULATE_ASSESSMENT: {
        this.assessment.status = AssessmentEngine.STATE_RETURN_TO_PARENT_ASSESSMENT;
        break;
      }

      default: {
        throw new Errors.AssessmentInvalidStateError(`invalid state: ${this.assessment.status}`);
      }
    }

    return this.assessment.status;
  }

  public getMemoryQuestionPromptTimeout(): number {
    return this.assessment.aptitudeSectionConfiguration.memoryQuestionPromptTimeout;
  }

  /**
   * Checks if the timer is enabled for this assessment.
   * @returns true if the timeout has a value, false otherwise
   */
  public isTimerEnabled(): boolean {
    return this.assessment.aptitudeSectionConfiguration?.timeout !== null;
  }

  public getIntroPages(): Api.IntroductionPageFullRead[] {
    return this.assessment.introductionPages;
  }
}
